import React, { useEffect, useState } from "react";
import api from "../../api";
import styles from "./TeamMeetingView.module.css";

const STATUS_CLASS = {
  완료: styles.done,
  진행중: styles.progress,
  미진행: styles.todo,
};

export default function TeamMeetingView() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusMode, setFocusMode] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [editingCommentUserId, setEditingCommentUserId] = useState(null); // ✅ 인라인 수정 중인 팀원
  const [editingCommentText, setEditingCommentText] = useState("");


  const fetchMeeting = async (targetDate) => {
    setLoading(true);
    try {
      const res = await api.get("/daily-todo-lists/meeting-view/", {
        params: { date: targetDate },
      });
      setMembers(res.data);
      setFocusIndex(0);
      setCommentDrafts({});   // ✅ 날짜/데이터 갱신 시 임시 입력 초기화
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeeting(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleStatusChange = async (itemId, status) => {
    await api.patch(`/daily-todo-items/${itemId}/`, { status });
    fetchMeeting(date);
  };

  const handleCommentSave = async (userId) => {
    const content = (commentDrafts[userId] ?? "").trim();
    if (!content) return; // 빈 입력이면 저장 안 함
    await api.post("/leader-comments/", { target_user: userId, date, content });
    fetchMeeting(date); // fetchMeeting이 commentDrafts를 비우므로 입력창은 자동으로 새 창이 됨
  };

  // ✅ 코멘트 수정: 저장된 박스가 그 자리에서 입력창으로 바뀜
  const handleCommentEdit = (member) => {
    setEditingCommentUserId(member.user_id);
    setEditingCommentText(member.leader_comment || "");
  };

  const handleCommentEditCancel = () => {
    setEditingCommentUserId(null);
    setEditingCommentText("");
  };

  const handleCommentEditSave = async (member) => {
    const content = editingCommentText.trim();
    if (!content) return;
    await api.post("/leader-comments/", { target_user: member.user_id, date, content });
    setEditingCommentUserId(null);
    setEditingCommentText("");
    fetchMeeting(date);
  };

  // ✅ 코멘트 삭제
  const handleCommentDelete = async (member) => {
    if (!member.leader_comment_id) return;
    if (!window.confirm("이 코멘트를 삭제하시겠어요?")) return;
    await api.delete(`/leader-comments/${member.leader_comment_id}/`);
    fetchMeeting(date);
  };

  
  const handleExcelDownload = async () => {
    const res = await api.get("/daily-todo-lists/export-excel/", {
      params: { date },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `daily_todo_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (loading) return <div className={styles.loading}>불러오는 중...</div>;

  const renderCard = (member) => (
    <div className={styles.card} key={member.user_id}>
      <div className={styles.cardHeader}>
        <span className={styles.userName}>{member.user_name}</span>
        <span className={styles.date}>{member.date}</span>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>우선</th>
            <th>업무명</th>
            <th>내용</th>
            <th>난이도</th>
            <th>예상(분)</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody>
          {member.items.map((item) => (
            <tr key={item.id} className={item.is_carried_over ? styles.carriedRow : ""}>
              <td>{item.priority}</td>
              <td>
                {item.task_name}
                {item.is_carried_over && <span className={styles.carryBadge}>이월</span>}
              </td>
              <td className={styles.detailCell}>{item.task_detail}</td>



              <td>{item.difficulty}</td>
              <td>{item.estimated_minutes || "-"}</td>
              <td>
                <select
                  value={item.status}
                  className={STATUS_CLASS[item.status]}
                  onChange={(e) => handleStatusChange(item.id, e.target.value)}
                >
                  <option value="미진행">미진행</option>
                  <option value="진행중">진행중</option>
                  <option value="완료">완료</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>


      <div className={styles.commentArea}>

        {member.leader_comment && (
          <div className={styles.existingComment}>
            {editingCommentUserId === member.user_id ? (
              <>
                <textarea
                  className={styles.inlineEditTextarea}
                  value={editingCommentText}
                  autoFocus
                  onChange={(e) => setEditingCommentText(e.target.value)}
                />
                <div className={styles.commentBtns}>
                  <button onClick={() => handleCommentEditSave(member)} className={styles.commentEditBtn}>
                    저장
                  </button>
                  <button onClick={handleCommentEditCancel} className={styles.commentDeleteBtn}>
                    취소
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.existingCommentText}>{member.leader_comment}</div>
                <div className={styles.commentBtns}>
                  <button onClick={() => handleCommentEdit(member)} className={styles.commentEditBtn}>
                    수정
                  </button>
                  <button onClick={() => handleCommentDelete(member)} className={styles.commentDeleteBtn}>
                    삭제
                  </button>
                </div>
              </>
            )}
          </div>
        )}




        <div className={styles.commentSection}>
          <textarea
            key={`${member.user_id}-${date}`}
            placeholder="팀장 코멘트 입력..."
            value={commentDrafts[member.user_id] ?? ""}
            onChange={(e) =>
              setCommentDrafts((prev) => ({ ...prev, [member.user_id]: e.target.value }))
            }
          />
          <button onClick={() => handleCommentSave(member.user_id)}>코멘트 저장</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>팀 회의 모드 - 오늘의 업무 현황</h2>
        <div className={styles.headerActions}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />

          <button onClick={() => setFocusMode((f) => !f)} className={styles.toggleBtn}>
            {focusMode ? "전체 보기" : <>포커스 모드<br />(프로젝터용)</>}
          </button>

        
          <button onClick={handleExcelDownload} className={styles.excelBtn}>
            엑셀 다운로드
          </button>
        </div>
      </div>

      {members.length === 0 && <div className={styles.empty}>오늘 등록된 업무가 없습니다.</div>}

      {!focusMode && <div className={styles.grid}>{members.map(renderCard)}</div>}

      {focusMode && members.length > 0 && (
        <div className={styles.focusWrapper}>
          <button
            className={styles.navBtn}
            onClick={() => setFocusIndex((i) => Math.max(0, i - 1))}
            disabled={focusIndex === 0}
          >
            ◀ 이전 팀원
          </button>
          <div className={styles.focusCard}>{renderCard(members[focusIndex])}</div>
          <button
            className={styles.navBtn}
            onClick={() => setFocusIndex((i) => Math.min(members.length - 1, i + 1))}
            disabled={focusIndex === members.length - 1}
          >
            다음 팀원 ▶
          </button>
        </div>
      )}
    </div>
  );
}